import React from "react";
import copy from "copy-to-clipboard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "~/components/shared/DropdownMenu";
import Icon from "~/components/shared/Icon";
import { LogSettingsContext } from "~/components/shared/Log";
import JobLogLineStore from "~/stores/JobLogLineStore";

type CopiedAction = "lines" | "permalink" | null;

/**
 * Dropdown menu for log line actions (copy lines, copy permalink).
 *
 * This component is only rendered when Features.LogViewerCopyLinesAction is true,
 * which implies Features.LogViewerMultiLineSelection is also enabled (enforced at the server level in _features_javascript.html.erb).
 */
export function LogLineActionsDropdown({
  jobId,
  lineNumber,
}: {
  jobId: string;
  lineNumber: number;
}) {
  const [copiedAction, setCopiedAction] = React.useState<CopiedAction>(null);
  const logSettings = React.useContext(LogSettingsContext);

  // Check if this line is part of a multi-line range
  const isLineInRange = JobLogLineStore.isLineInRange(jobId, lineNumber);
  const range = isLineInRange ? JobLogLineStore.getHighlightedRange(jobId) : null;

  const linesLabel = isLineInRange ? "Copy lines" : "Copy line";
  const triggerLabel = range
    ? `Options for lines ${range.start} to ${range.end}`
    : `Options for line ${lineNumber}`;

  const handleCopyLines = React.useCallback(() => {
    const text = JobLogLineStore.getHighlightedLinesText(jobId, logSettings);
    copy(text);
    setCopiedAction("lines");

    setTimeout(() => {
      setCopiedAction(null);
    }, 1500);
  }, [jobId, logSettings]);

  const handleCopyPermalink = React.useCallback(() => {
    const url = new URL(window.location.href);

    // Check if we have a range highlighted
    const range = JobLogLineStore.getHighlightedRange(jobId);
    if (range && range.start !== range.end) {
      // Range format: #jobId/L{start}-L{end}
      url.hash = `${jobId}/L${range.start}-L${range.end}`;
    } else {
      // Single line format: #jobId/L{lineNumber}
      url.hash = `${jobId}/L${lineNumber}`;
    }

    copy(url.toString());
    setCopiedAction("permalink");
    setTimeout(() => setCopiedAction(null), 1500);
  }, [jobId, lineNumber]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild={true}>
        <button
          type="button"
          aria-label={triggerLabel}
          className="btn btn-depth btn-small before:shadow-depth-150 no-title-tooltip relative flex h-6 w-6 items-center justify-center bg-white p-0"
        >
          {copiedAction !== null ? (
            <Icon icon="heroicons/16/solid/check" className="size-4 text-green-700" />
          ) : (
            <Icon icon="heroicons/16/solid/ellipsis-horizontal" className="size-4" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent align="start" side="bottom">
          <DropdownMenuItem onSelect={handleCopyLines}>{linesLabel}</DropdownMenuItem>
          <DropdownMenuItem onSelect={handleCopyPermalink}>Copy permalink</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
